import videojs from 'video.js';
import { ensureYoutubeIframeApi } from '../lib/yt-iframe-api';

function playGlyphSvg(): string {
  return `<svg class="fc-media-start__glyph" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48" focusable="false" aria-hidden="true">
  <circle cx="24" cy="24" r="21" fill="currentColor" opacity="0.95" />
  <polygon class="fc-media-start__glyph-play" points="18,15 18,33 34,24" />
</svg>`;
}

interface YtApiPlayer {
  playVideo: () => void;
}

const ytPlayerByIframe = new WeakMap<HTMLIFrameElement, YtApiPlayer>();

/**
 * BEM modifier na warstwie gradientu/typo nad playerem w video-showcase (`fc-video-showcase__hero-overlay`).
 * Nazwa elementu BEM jest historyczna — nie dotyczy komponentu strony `hero-banner`.
 */
const VIDEO_SHOWCASE_OVERLAY_PLAYING_CLASS = 'fc-video-showcase__hero-overlay--playing';

/** Synchronizacja gradientu/typo nad playerem z odtwarzaniem (video-showcase). No-op poza [data-fc-showcase]. */
export function syncVideoShowcaseOverlayPlayback(container: HTMLElement, playing: boolean): void {
  const showcase = container.closest<HTMLElement>('[data-fc-showcase]');
  const overlay = showcase?.querySelector<HTMLElement>('[data-fc-showcase-overlay]');
  if (!overlay) {
    return;
  }
  overlay.classList.toggle(VIDEO_SHOWCASE_OVERLAY_PLAYING_CLASS, playing);
  if (playing) {
    overlay.setAttribute('aria-hidden', 'true');
  } else {
    overlay.removeAttribute('aria-hidden');
  }
}

/** YouTube IFrame API: PLAYING=1, BUFFERING=3 — overlay ukryty; reszta = widać warstwę nad playerem. */
function isYoutubeActivePlaybackState(data: number): boolean {
  return data === 1 || data === 3;
}

function hideYtChromeMaskIfPresent(container: HTMLElement): void {
  const mask = container.querySelector<HTMLElement>('[data-fc-showcase-yt-chrome-mask]');
  if (mask) {
    mask.hidden = true;
  }
}

/** Po init Video.js klasa `video-js` jest na `div`, a `<video>` zostaje jako `.vjs-tech` — szukaj po `data-fc-vjs`. */
function resolveVideoJsPlayer(container: HTMLElement): ReturnType<typeof videojs> | undefined {
  const root = container.querySelector<HTMLElement>('.video-js');
  if (root?.id) {
    try {
      return videojs.getPlayer(root.id);
    } catch {
      /* noop */
    }
  }
  const tech = container.querySelector<HTMLVideoElement>('video[data-fc-vjs]');
  if (tech) {
    try {
      return videojs.getPlayer(tech);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Wspólny „poster” z ikoną play nad YouTube iframe i Video.js do pierwszego startu odtwarzania.
 */
export function attachFcMediaStartOverlay(container: HTMLElement): void {
  if (container.querySelector('[data-fc-media-start-overlay]')) {
    return;
  }

  const iframe = container.querySelector<HTMLIFrameElement>('iframe.fc-youtube__iframe');
  /* Przed init: `<video class="video-js">`; po init: samo `video[data-fc-vjs]` (tech) */
  const videoEl = container.querySelector<HTMLVideoElement>('video[data-fc-vjs]');

  if (!iframe && !videoEl) {
    return;
  }

  const hit = document.createElement('button');
  hit.type = 'button';
  hit.className = 'fc-media-start__overlay';
  hit.setAttribute('data-fc-media-start-overlay', '');
  hit.setAttribute('aria-label', container.dataset.fcAriaPlayLabel?.trim() || 'Play video');
  hit.innerHTML = playGlyphSvg();
  container.appendChild(hit);

  const dismiss = (): void => {
    hit.hidden = true;
    hideYtChromeMaskIfPresent(container);
  };

  if (iframe) {
    /**
     * Track iframe load state. The iframe may have loading="lazy" and might not have
     * fetched YouTube content yet. We wait for the load event before sending postMessage
     * commands to the player.
     */
    let iframeLoaded = false;
    iframe.addEventListener(
      'load',
      () => {
        iframeLoaded = true;
      },
      { once: true },
    );

    const waitForIframe = (): Promise<void> => {
      if (iframeLoaded) {
        return Promise.resolve();
      }
      // Remove lazy loading to force the browser to fetch if not yet started
      iframe.removeAttribute('loading');
      return new Promise<void>((resolve) => {
        if (iframeLoaded) {
          resolve();
          return;
        }
        iframe.addEventListener('load', () => resolve(), { once: true });
      });
    };

    /**
     * Listen for YouTube player state changes to sync the hero overlay.
     * Registered once per iframe instance; becomes a no-op when iframe is
     * removed from the DOM (iframe.contentWindow becomes null).
     */
    window.addEventListener('message', (e: MessageEvent) => {
      if (!iframe.contentWindow || e.source !== iframe.contentWindow) {
        return;
      }
      try {
        const data =
          typeof e.data === 'string'
            ? (JSON.parse(e.data) as { event?: string; info?: number })
            : (e.data as { event?: string; info?: number });
        if (data?.event === 'onStateChange') {
          syncVideoShowcaseOverlayPlayback(
            container,
            isYoutubeActivePlaybackState(data.info ?? -1),
          );
        }
      } catch {
        /* noop */
      }
    });

    hit.addEventListener('click', (e) => {
      e.preventDefault();

      // Use cached YT.Player if available from a previous successful registration.
      const cached = ytPlayerByIframe.get(iframe);
      if (cached) {
        try {
          cached.playVideo();
        } finally {
          dismiss();
        }
        return;
      }

      hit.disabled = true;
      void waitForIframe()
        .then(() => {
          /**
           * Play via direct postMessage – reliable regardless of whether
           * www-widgetapi.js has already processed the iframe's initialDelivery
           * event and pre-claimed the player (which causes new YT.Player() to
           * return alreadyInitialized without firing onReady).
           */
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            'https://www.youtube.com',
          );
          dismiss();
          hit.disabled = false;

          // Opportunistically wire up a YT.Player instance for richer API access
          // on future interactions (e.g. cached playVideo, full state callbacks).
          void ensureYoutubeIframeApi().then(() => {
            const YT = (
              window as unknown as {
                YT?: {
                  Player: new (
                    el: HTMLElement,
                    opts: {
                      events?: {
                        onReady?: (ev: { target: YtApiPlayer }) => void;
                        onError?: () => void;
                        onStateChange?: (ev: { data: number }) => void;
                      };
                    },
                  ) => unknown;
                };
              }
            ).YT;
            if (!YT?.Player || ytPlayerByIframe.get(iframe)) {
              return;
            }
            try {
              new YT.Player(iframe, {
                events: {
                  onReady: (ev: { target: YtApiPlayer }): void => {
                    ytPlayerByIframe.set(iframe, ev.target);
                  },
                  // State changes are tracked by the window message listener above.
                  onStateChange: (): void => {
                    /* handled above */
                  },
                  onError: (): void => {
                    /* noop */
                  },
                },
              });
            } catch {
              /* noop */
            }
          });
        })
        .catch(() => {
          dismiss();
          hit.disabled = false;
        });
    });
    return;
  }

  if (videoEl) {
    let vjsWired = false;
    const wire = (): boolean => {
      if (vjsWired) {
        return true;
      }
      const vjs = resolveVideoJsPlayer(container);
      if (!vjs) {
        return false;
      }
      vjsWired = true;
      hit.addEventListener('click', (e) => {
        e.preventDefault();
        void Promise.resolve(vjs.play()).then(dismiss).catch(dismiss);
      });
      vjs.on('play', () => {
        dismiss();
        syncVideoShowcaseOverlayPlayback(container, true);
      });
      vjs.on('pause', () => {
        syncVideoShowcaseOverlayPlayback(container, false);
      });
      vjs.on('ended', () => {
        syncVideoShowcaseOverlayPlayback(container, false);
      });
      return true;
    };

    if (!wire()) {
      let n = 0;
      const id = window.setInterval(() => {
        n += 1;
        if (wire() || n > 60) {
          window.clearInterval(id);
          if (n > 60 && !vjsWired) {
            hit.addEventListener('click', (e) => {
              e.preventDefault();
              dismiss();
            });
          }
        }
      }, 50);
    }
  }
}

/** Bloki DAM w komponencie video (bez data-fc-media-embed-url) — shell zawiera już video-js. */
export function initFcMediaStartOverlaysForDamVideoBlocks(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('.fc-video__shell--native').forEach((shell) => {
    attachFcMediaStartOverlay(shell);
  });
}
